import { useCallback, useEffect, useRef, useState } from 'react';
import type { ShowcaseScreen } from './useShowcaseData';

interface UseAutoCycleOptions {
  loop?: boolean;
  transitionDurationMs?: number;
}

interface UseAutoCycleReturn {
  currentIndex: number;
  isTransitioning: boolean;
  direction: 'forward';
}

/**
 * Auto-cycling engine for showcase screen playback.
 *
 * Each screen has its own `dwellTimeSeconds`. When the dwell timer fires,
 * `isTransitioning` goes true for `transitionDurationMs`, then currentIndex
 * advances. If `loop` is true, wraps to 0 at the end. Otherwise stays on
 * the last screen.
 *
 * Mouse activity pauses cycling for 10 seconds (useful for debug/demo).
 */
export function useAutoCycle(
  screens: ShowcaseScreen[],
  options: UseAutoCycleOptions = {},
): UseAutoCycleReturn {
  const { loop = true, transitionDurationMs = 800 } = options;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const pausedUntilRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const screenCount = screens.length;

  // Clamp index when screen count shrinks (e.g. admin removes a screen during playback)
  useEffect(() => {
    if (screenCount > 0 && currentIndex >= screenCount) {
      setCurrentIndex(screenCount - 1);
    }
  }, [screenCount, currentIndex]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
  }, []);

  // Schedule the next transition
  const scheduleNext = useCallback(() => {
    if (screenCount <= 1) return;

    clearTimers();

    const currentScreen = screens[currentIndex];
    if (!currentScreen) return;

    const dwellMs = currentScreen.dwellTimeSeconds * 1000;

    timerRef.current = setTimeout(() => {
      // Check if paused by mouse activity
      if (Date.now() < pausedUntilRef.current) {
        // Re-schedule after pause expires
        const remaining = pausedUntilRef.current - Date.now();
        timerRef.current = setTimeout(() => {
          scheduleNext();
        }, remaining);
        return;
      }

      // Check if we're at the end and shouldn't loop
      const isLastScreen = currentIndex === screenCount - 1;
      if (isLastScreen && !loop) return;

      // Begin transition
      setIsTransitioning(true);

      transitionTimerRef.current = setTimeout(() => {
        setCurrentIndex((prev) => {
          const next = prev + 1;
          return next >= screenCount ? 0 : next;
        });
        setIsTransitioning(false);
      }, transitionDurationMs);
    }, dwellMs);
  }, [screenCount, screens, currentIndex, loop, transitionDurationMs, clearTimers]);

  // Schedule cycling whenever currentIndex or screens change
  useEffect(() => {
    scheduleNext();
    return clearTimers;
  }, [scheduleNext, clearTimers]);

  // Pause on mouse activity (10-second debounce)
  useEffect(() => {
    const handleMouseMove = () => {
      pausedUntilRef.current = Date.now() + 10_000;
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return {
    currentIndex,
    isTransitioning,
    direction: 'forward',
  };
}
